import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CourseCategory, listCourseCategories } from 'src/services/categories';

const ENABLED_CATEGORY_ORDER = [
  'cfp',
  'cfg',
  'cga',
  'cge',
  'ancord',
  'cnpi',
  'cnpi-cg',
  'cnpi-ct',
  'cfa-l1',
  'cpa',
  'c-pro-i',
  'c-pro-r',
] as const;

const ENABLED_CATEGORY_INDEX = new Map<string, number>(
  ENABLED_CATEGORY_ORDER.map((slug, index) => [slug, index]),
);

function normalizeSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type CourseCategoriesContextValue = {
  categories: CourseCategory[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const CourseCategoriesContext = createContext<CourseCategoriesContextValue | null>(null);

export function CourseCategoriesProvider({ children }: PropsWithChildren<{}>) {
  const location = useLocation();
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCourseCategories();
      const unique = new Map<string, CourseCategory>();

      data.forEach(category => {
        const key = normalizeSlug(category.slug ?? category.name ?? '');
        if (!key) {
          return;
        }
        if (!ENABLED_CATEGORY_INDEX.has(key)) {
          return;
        }
        if (!unique.has(key)) {
          unique.set(key, {
            ...category,
            slug: key,
          });
        }
      });

      const normalizedCategories = Array.from(unique.values()).sort((left, right) => {
        const leftIndex = ENABLED_CATEGORY_INDEX.get(left.slug) ?? Number.MAX_SAFE_INTEGER;
        const rightIndex = ENABLED_CATEGORY_INDEX.get(right.slug) ?? Number.MAX_SAFE_INTEGER;
        return leftIndex - rightIndex;
      });

      setCategories(normalizedCategories);
      setError(null);
    } catch (err: any) {
      setError('Não foi possível carregar as famílias de certificação.');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const isAuthRoute = location.pathname.startsWith('/auth');

    if (!token) {
      setCategories([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (isAuthRoute) {
      setLoading(false);
      return;
    }

    void fetchCategories();
  }, [fetchCategories, location.pathname]);

  const value = useMemo(
    () => ({
      categories,
      loading,
      error,
      refresh: fetchCategories,
    }),
    [categories, error, fetchCategories, loading],
  );

  return (
    <CourseCategoriesContext.Provider value={value}>
      {children}
    </CourseCategoriesContext.Provider>
  );
}

export function useCourseCategories() {
  const context = useContext(CourseCategoriesContext);
  if (!context) {
    throw new Error('useCourseCategories must be used within a CourseCategoriesProvider');
  }
  return context;
}
